import type { MeResponse, UserSettings } from '@zinc/shared';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Client errors (401, 404, …) won't change on retry.
        retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
        staleTime: 30_000,
      },
    },
  });
}

export const queryKeys = {
  me: ['me'] as const,
};

export function useMe() {
  return useQuery({ queryKey: queryKeys.me, queryFn: () => api<MeResponse>('/api/me') });
}

/** Saves one or more settings; the screen updates at once and rolls back if saving fails. */
export function useSaveSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['settings'],
    mutationFn: (patch: Partial<UserSettings>) =>
      api<UserSettings>('/api/me/settings', { method: 'PATCH', json: patch }),
    onMutate: async (patch) => {
      await client.cancelQueries({ queryKey: queryKeys.me });
      const before = client.getQueryData<MeResponse>(queryKeys.me);
      if (before) {
        client.setQueryData<MeResponse>(queryKeys.me, {
          ...before,
          settings: { ...before.settings, ...patch },
        });
      }
      return { before };
    },
    onError: (_err, _patch, context) => {
      if (context?.before) client.setQueryData(queryKeys.me, context.before);
    },
    onSuccess: (settings) => {
      // While another change is still on its way, this answer may predate it: keep the screen.
      if (client.isMutating({ mutationKey: ['settings'] }) > 1) return;
      client.setQueryData<MeResponse>(queryKeys.me, (me) => (me ? { ...me, settings } : me));
    },
  });
}
